import { formatDate } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, iif, merge, of, throwError } from 'rxjs';
import { catchError, concatMap, map, take, tap, toArray } from 'rxjs/operators';
import { Department, DeptBookService } from 'src/app/service/deptbook.service';
import { NavigationService } from 'src/app/service/navigation.service';
import { QRCodeGenerationService } from 'src/app/service/qrcode-generation.service';
import { NetworkService } from 'src/app/service/network.service';
import { NotificationService } from 'src/app/service/notification-service';
import { AppService } from 'src/app/app.service';
import { RequestRoot } from 'types';
import { StatusColorMapper } from 'src/app/service/status-color-mapper';
import { InfoSiteRequest } from 'src/app/model/info-site-request';
import { DeleteCommand } from 'src/app/command/delete-command';
import { QRCodeData } from 'src/app/model/qrcode-data';
import { AppContext, Command, CommandContext, CommandPage, CommandSideEffects, CommandWrapper, FeaturesI18N } from 'controls';
import { RequestFeatures } from 'src/webid/4.0/model/features';
import { RequestFeaturesV2 } from 'src/webid/4.0/model/features2';
import { RequestDataAccessService } from 'src/webid/4.0/projects/data-access/src/lib/request-data-access.service';
import { InternetConnectionState } from 'src/app/model/internet-connection-state';
import { SubscriptionService } from 'src/app/service/subscription.service';
import { DialogService } from 'src/app/service/dialog.service';

type LoadingState = 'PENDING' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED';

@Component({
    selector: 'request-preview',
    templateUrl: './request-preview.component.html',
    styleUrls: ['./request-preview.component.scss'],
})
export class RequestPreviewComponent extends CommandPage implements OnInit {
    public loadingState: LoadingState = 'PENDING';
    public request: RequestRoot | null = null;
    public status: InfoSiteRequest | null = null;
    public department: Department | null = null;
    public qrCode: string | null = null;
    private deleteCommand: Command<RequestRoot> | null = null;
    private internetStatus: InternetConnectionState | null = null;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private appContext: AppContext,
        private cmdContext: CommandContext,
        private appService: AppService,
        private networkService: NetworkService,
        private deptBookService: DeptBookService,
        private navigationService: NavigationService,
        private requestService: RequestDataAccessService,
        private notificationService: NotificationService,
        private qrCodeGenerationService: QRCodeGenerationService,
        private subscriptionService: SubscriptionService,
        private dialogService:  DialogService,
        @Inject(LOCALE_ID) private locale: string
    ) {
        super();
    }

    ngOnInit(): void {
        this.setupActivitySettings();
        this.getCommandCompleted$().subscribe((r: CommandSideEffects) => {
            this.onCommandCompleted(r);
        });
        this.deleteCommand = new DeleteCommand('command.request.delete', this.notificationService);
        this.networkService.internet$.subscribe((status) => (this.internetStatus = status));
        this.reloadDequestData().subscribe();
    }

    refresh(): void {
        this.resetComponentState();
        this.reloadDequestData().subscribe();
    }

    edit(): void {
        let params = { queryParams: { id: this.request?.id } };
        this.router.navigate(['/request-editor'], params);
    }

    delete(): void {
        if (this.deleteCommand) {
            this.executeCommand(this.deleteCommand);
        }
    }

    subscribe(): void {
        let params = { queryParams: { id: this.request?.id } };
        this.router.navigate(['/subscription-via-barcode'], params);
    }

    print(): void {
        this.dialogService.closeOpenedNotifications();
            let params = { queryParams: { id: this.request?.id } };
            this.router.navigate(['/print'], params);

    }

    showQrCode(): void {
        this.appService.qrCodeImage = new QRCodeData();
        this.appService.qrCodeImage.fullName = this.formatFullName();
        this.appService.qrCodeImage.birthDate = this.formatBirthDate();
        this.appService.qrCodeImage.url = this.qrCode;
        this.router.navigate(['/request-qrcode']);
    }

    formatFullName(): string {
        let features: RequestFeatures | RequestFeaturesV2 = this.request?.features;
        return features.applicant.personIdentity.fullName.asString();
    }

    formatBirthDate(): string {
        let features: RequestFeatures | RequestFeaturesV2 = this.request?.features;
        let birthDate = features.applicant.personIdentity.birthDate;

        return birthDate.value.endsWith('-00-00')
            ? '00.00.' + birthDate.value.substring(0, 4)
            : formatDate(birthDate.toUTCDate() ?? '', 'dd.MM.yyyy', this.locale);
    }

    get isWaitingForInternetConnection(): boolean {
        return (
            this.request !== null &&
            this.loadingState === 'PENDING' &&
            this.internetStatus === InternetConnectionState.OFFLINE &&
            (this.isDepartmentInfoLoadingRequired || (this.isAcceptedInKZU && this.status == null))
        );
    }

    get isDepartmentInfoLoadingRequired(): boolean {
        return this.appService.departments === null && !this.request?.features?.mobileOptions?.departmentCountry;
    }

    get isApplicationPresent(): boolean {
        return Boolean(this.request?.eventHistory?.some((event) => event.type === 'READY'));
    }

    get isAcceptedInKZU(): boolean {
        return this.request?.status !== 'DATA_ENTERED' && this.request?.status !== 'READY';
    }

    get isProgressVisible() {
        return (
            this.request?.status === 'INFO_IN_PROGRESS' ||
            this.request?.status === 'INFO_DOCUMENTS_IN_PROCESSING' ||
            this.request?.status === 'INFO_PENDING_DATA_PROCESSING'
        );
    }

    get statusName(): string {
        return FeaturesI18N.getString('STATUS.BIOPASSPORT.' + this.status?.passportStatus.code);
    }

    get statusColor(): string {
        const code = this.status?.passportStatus.code;
        return code ? `#${StatusColorMapper.getStatusColor(code).hex()}` : 'trasparent';
    }

    get receptionDate(): string {
        const date = this.status?.receptionDate;
        return date ? formatDate(date, 'dd.MM.yyyy', this.locale) : 'Дата не указана';
    }

    private reloadDequestData(): Observable<void> {
        return this.route.queryParams.pipe(take(1)).pipe(
            concatMap((params) => this.getPrimaryRequestData(params['id'])),
            concatMap(() => this.getExternalRequestData()),
            concatMap(() => this.onLoadingSucceeded()),
            catchError(() => this.onLoadingFailed())
        );
    }

    private getPrimaryRequestData(id: string): Observable<void> {
        const requestRootBuffer = new RequestRoot();
        requestRootBuffer.id = id;

        return this.requestService.load(requestRootBuffer).pipe(
            tap((r) => (this.request = r)),
            map(() => void 0)
        );
    }

    private getExternalRequestData(): Observable<void> {
        let department$: Observable<void>;
        let features: RequestFeaturesV2 | RequestFeaturesV2 = this.request?.features;
        let isDepartmentPresent = !!features.mobileOptions.departmentCountry;

        if (isDepartmentPresent) {
            department$ = of(features).pipe(
                tap((f) => (this.department = this.createDepartmentFromFeatures(f))),
                tap((f) => {
                    this.setupRequestQRCode();
                }),
                map((f) => {})
            );
        } else {
            let code = features.mobileOptions.department;
            department$ = this.getDepartmentList().pipe(
                tap((list) => (this.department = list.find((d) => d.code === code) ?? null)),
                tap(() => this.setupRequestQRCode()),
                map(() => {})
            );
        }

        const status$ = this.networkService.waitForInternetConnection().pipe(
            concatMap(() => this.onLoadingStarted()),
            concatMap(() =>
                iif(
                    () => Boolean(this.request?.id),
                    this.subscriptionService.updateSubscriptionStatus(this.request?.id!, true),
                    throwError(() => new Error('Отсутсвует ID заявления'))
                )
            ),
            tap((result) => {
                if (result) {
                    this.request = result.request;
                    this.status = result.info.actual();
                }
            })
        );

        const both$ = merge(department$, status$).pipe(
            toArray(),
            map(() => void 0)
        );

        return this.isAcceptedInKZU ? both$ : department$;
    }

    getDepartmentList(): Observable<Department[]> {
        return this.appService.departments === null ? this.loadDepartmentListFromServer() : of(this.appService.departments);
    }

    private loadDepartmentListFromServer(): Observable<Department[]> {
        return this.onLoadingStarted().pipe(
            concatMap(() => this.deptBookService.list()),
            tap((list) => (this.appService.departments = list))
        );
    }

    private setupRequestQRCode(): void {
        if (!this.request || !this.department) {
            console.error('Заявление или ТУ не найдено');
            return;
        }

        let result = this.qrCodeGenerationService.getPlainQrCode(this.request, this.department);

        if (typeof result === 'string') {
            this.qrCode = result;
        }
    }

    private onCommandCompleted(effects: CommandSideEffects): void {
        if (effects.databaseModified) {
            this.navigationService.navigateBack();
        }
    }

    private onLoadingStarted(): Observable<void> {
        this.showActivityLoader('Загрузка данных');
        this.loadingState = 'IN_PROGRESS';
        return of(void 0);
    }

    private onLoadingFailed(): Observable<void> {
        this.loadingState = 'FAILED';
        this.hideActivityLoader();
        return of(void 0);
    }

    private onLoadingSucceeded(): Observable<void> {
        this.loadingState = 'SUCCEEDED';
        this.hideActivityLoader();
        return of(void 0);
    }

    private showActivityLoader(message: string): void {
        this.navigationService.activity.loader.show(message);
    }

    private hideActivityLoader(): void {
        this.navigationService.activity.loader.hide();
    }

    private setupActivitySettings(): void {
        this.navigationService.activity.reset();
        this.navigationService.activity.toolbar.setTitle('Просмотр заявления');
    }

    private executeCommand(command: Command<RequestRoot>): void {
        if (!this.request) {
            console.error('Заявление не найдено');
            return;
        }

        let wrapper = new CommandWrapper(this.appContext, this.cmdContext, this, command);

        wrapper.execute([this.request]);
    }

    private resetComponentState(): void {
        this.loadingState = 'PENDING';
        this.request = null;
        this.qrCode = null;
        this.department == null;
        this.status == null;
    }

    private createDepartmentFromFeatures(features: RequestFeatures | RequestFeaturesV2): Department {
        let department = new Department();

        department.code = features.mobileOptions.department;
        department.country = features.mobileOptions.departmentCountry;
        department.city = features.mobileOptions.departmentCity;

        return department;
    }
}
